import { useState } from "react";
import { DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { useSeo } from "@/lib/seo";

export default function ThumbnailDownloader() {
  useSeo("YouTube Thumbnail Downloader", "Download any YouTube video thumbnail in HD, SD and every other resolution — free.");
  const [url, setUrl] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchThumbs = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    try {
      const res = await api.get("/thumbnails", { params: { url } });
      setData(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="thumbnail-downloader" title="Thumbnail Downloader" tagline="Paste any YouTube video link and grab its thumbnail in every available resolution." hasResults={!!data}>
      <form onSubmit={fetchThumbs} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Video URL</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="thumbnail-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="thumbnail-fetch-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <MagnifyingGlass size={16} weight="bold" /> {loading ? "Fetching..." : "Get thumbnails"}
          </button>
        </div>
      </form>

      {data && (
        <div className="mt-8 fade-up">
          {data.title && <h2 className="text-lg font-bold mb-4" data-testid="thumbnail-video-title">{data.title}</h2>}
          <div className="grid sm:grid-cols-2 gap-4">
            {data.thumbnails.map((t) => (
              <div key={t.label} className="surface rounded-[20px] overflow-hidden lift">
                <img src={t.url} alt={t.label} className="w-full aspect-video object-cover" loading="lazy" />
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{t.label}</p>
                    <p className="text-[var(--muted)] text-xs">{t.size}</p>
                  </div>
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--coral)] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                  >
                    <DownloadSimple size={14} weight="bold" /> Open
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </ToolShell>
  );
}
